import {
  Button,
  Center,
  FormLabel,
  Heading,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { StartlistExtendedRow } from "../../../../Api/dto/StartlistDto";
import { SignupRequest } from "../../../../Api/dto/SignupRequest_POST";
import { Role, useSessionState } from "../../../../Session/SessionService";
import { Gender } from "../../../../Api/dto/Discipline_POST_PUT";
import { Discipline_GET } from "../../../../Api/dto/Discipline_GET";
import BirthDatePicker from "../../../DatePicker/DatePicker";
import { birthDateToString } from "../Step2SelectParticipants";

export function ModifySignupModal(props: {
  competitionTag: string;
  disciplines: Array<Discipline_GET>;
  editRow: StartlistExtendedRow | null;
  onUpdate: (newSignupValues: SignupRequest, signupId: number) => Promise<any>;
  onUpdateGroupName: (
    oldName: string,
    newName: string,
    paymentId: number
  ) => Promise<any>;
  onDelete: (signupId: number) => Promise<any>;
  onRestoreDeleted: (signupId: number) => Promise<any>;
  onClose: (updatedSignup: boolean) => void;
}) {
  const [discipline, setDiscipline] = useState<Discipline_GET | null>(null);
  const [firstname, setFirstname] = useState<string>("");
  const [lastname, setLastname] = useState<string>("");
  const [birthdate, setBirthdate] = useState<Date | null>(null);
  const [sportsteam, setSportsTeam] = useState<string>("");
  const [group, setGroup] = useState<string>("");
  const [startnumber, setStartnumber] = useState<number | null>(0);
  const [gender, setGender] = useState<Gender| null>(null);

  const session = useSessionState();

  const [enableDisciplineChange, setEnableDisciplineChange] = useState<boolean>(false);

  useEffect(() => {
    if (props.disciplines != null) {
      const d = props.disciplines.find(d => d.id === props.editRow?.disciplineId);
      setDiscipline(d ?? null);
    }

  }, [props.editRow?.disciplineId, props.disciplines]);


  useEffect(() => {
    const isAdmin = session.role != null && [Role.ADMIN, Role.OFFICIAL, Role.DEVELOPER].includes(session.role);
    const isNotTeamDiscipline = discipline?.groupSize == null || discipline.groupSize < 2;
    setEnableDisciplineChange(discipline != null && isAdmin && isNotTeamDiscipline && props.editRow?.startnumber == null);

    if (discipline != null) {
      const bYear = birthdate?.getFullYear() || props.editRow?.birthYear;

      const minAgeBirthYear = discipline.minAge == null ? null : new Date().getFullYear() - discipline.minAge;
      const maxAgeBirthYear = discipline.maxAge == null ? null : new Date().getFullYear() - discipline.maxAge;

      if (bYear != null) {
         if (discipline.id == props.editRow?.disciplineId && props.editRow.birthYear == birthdate?.getFullYear()) {
            // DO NOTHING HERE! Even invalid years should remain if an admin changed them.
         }
         else if (minAgeBirthYear && bYear > minAgeBirthYear) {
            setBirthdate(null);
         }
         else if (maxAgeBirthYear && bYear < maxAgeBirthYear) {
            setBirthdate(null);
         }
      }

      if (discipline.genderRequirements.length == 1) {
        setGender(discipline.genderRequirements[0]);
      }
      else if (gender != null && !discipline.genderRequirements.includes(gender)) {
        setGender(null);
      }
    }

  }, [discipline, session.role]);

  useEffect(() => {
    setFirstname(props.editRow?.firstname ?? "");
    setLastname(props.editRow?.lastName ?? "");
    setBirthdate(
      dateFromSocialSecurityNumber(props.editRow?.socialSecurityNumber) || 
      dateFromYear(props.editRow?.birthYear) || 
      null
    );
    setSportsTeam(props.editRow?.sportTeam ?? "");
    setGroup(props.editRow?.groupName ?? "");
    setStartnumber(props.editRow?.startnumber ?? null);
    setGender(props.editRow?.gender ?? null);
  }, [
    props.editRow?.firstname,
    props.editRow?.lastName,
    props.editRow?.birthYear,
    props.editRow?.socialSecurityNumber,
    props.editRow?.sportTeam,
    props.editRow?.groupName,
    props.editRow?.startnumber,
    props.editRow?.gender
  ]);


  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const isBelowSm = useBreakpointValue({ base: true, sm: false, });

  useEffect(() => {
    if (firstname.trim().length === 0) {
      setErrorMsg("Ange förnamn");
    } else if (lastname.trim().length === 0) {
      setErrorMsg("Ange efternamn");
    } else if (birthdate == null) {
      setErrorMsg("Ange födelsedatum");
    } else if (gender == null && (discipline?.genderRequirements.length ?? 0) !== 0) {
      setErrorMsg("Ange kön");
    } else if (props.editRow?.groupName != null && group.trim().length < 3) {
      setErrorMsg("Ange giltigt lagnamn");
    } 
    else {
      setErrorMsg(null);
    }
  }, [firstname, lastname, birthdate, sportsteam, group, gender, discipline]);

  if (props.editRow == null || props.editRow.signedupId == null) {
    return null;
  }
  return (
    <Modal isOpen={props.editRow != null} onClose={() => props.onClose(false)}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Ändra anmälan</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {props.editRow?.signedupSoftDeleted ? (
            <>
              <Heading size={"sm"}>Borttagen</Heading>
              <Text>
                Anmälan är borttagen och personen är struken från
                tävlingslistorna. Om detta var ett misstag så går det att
                återställa anmälan här.
              </Text>
              <Text fontFamily={"cursive"}>
                OBS: Informera målhuset om du återställer efter att anmälan har
                stängt för att försäkra dig om att de uppdaterar sina listor!
              </Text>
            </>
          ) : (
            <form>
              {enableDisciplineChange && <>
                  <FormLabel>Tävlingsklass</FormLabel>
                  <Select 
                    disabled={session.hasAtLeastRole(Role.OFFICIAL) ? false : true}
                    value={discipline?.id ?? undefined} 
                    onChange={(e) => setDiscipline(props.disciplines.find(d => d.id === Number(e.target.value)) ?? null)}>
                      {props.disciplines.filter(d => ((d.groupSize ?? 0) < 2)).map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                  </Select>
                </>
              }

              <FormLabel>Förnamn</FormLabel>
              <Input
                marginBottom={"20px"}
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
              ></Input>
              <FormLabel>Efternamn</FormLabel>
              <Input
                marginBottom={"20px"}
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
              ></Input>
              <FormLabel>Födelsedatum</FormLabel>
              <BirthDatePicker
                marginBottom={"20px"}
                direction={isBelowSm ? "column" : "row"}
                onChange={(date: Date | null) => {
                  setBirthdate(date);
                }}
                maxYear={new Date().getFullYear() - (discipline?.minAge ?? 0)}
                minYear={new Date().getFullYear() - (discipline?.maxAge ?? 200)}
                value={birthdate}
                allowSettingAnyBirthYear={session.hasAtLeastRole(Role.OFFICIAL)}
               />
              {discipline?.genderRequirements.length == 0 ||
                <>
              <FormLabel>Kön</FormLabel>
              <RadioGroup
                marginBottom={"20px"}
                marginLeft={"10px"}
                onChange={(e) => {
                  console.log("Setting " + e)
                  setGender(e === "MAN" ? Gender.MAN : Gender.WOMAN);
                }}
                value={
                  gender == Gender.MAN
                    ? "MAN"
                    : gender == Gender.WOMAN
                    ? "WOMAN"
                    : ""
                }
              >
                <Stack direction="column">
                  {discipline?.genderRequirements.map(g => {
                    if (g === Gender.MAN) {
                      return (<Radio key={"MAN"} borderColor={"gray.500"} value={"MAN"}>
                        Man/Pojke
                      </Radio>);
                    }
                    else if (g === Gender.WOMAN) {
                      return (<Radio key={"WOMAN"} borderColor={"gray.500"} value={"WOMAN"}>
                        Kvinna/Flicka
                      </Radio>);
                    }
                  }
                  )}
                </Stack>
              </RadioGroup>
              </>
              }

              <FormLabel>Klubb</FormLabel>
              <Input
                marginBottom={"20px"}
                value={sportsteam}
                onChange={(e) => setSportsTeam(e.target.value)}
              ></Input>
              <FormLabel>Lagnamn</FormLabel>
              <Input
                disabled={
                  !session.hasAtLeastRole(Role.OFFICIAL) ||
                  props.editRow?.groupName == null ||
                  props.editRow.groupName.trim() === ""
                }
                marginBottom={"20px"}
                value={group}
                onChange={(e) => setGroup(e.target.value)}
              ></Input>
              <FormLabel>Startnumber</FormLabel>
              <HStack>
                <Input
                  disabled={true}
                  marginBottom={"20px"}
                  value={startnumber ?? ""}
                ></Input>
              </HStack>
              <Center>
                <Text
                  visibility={errorMsg ? undefined : "hidden"}
                  textColor={"red"}
                  fontWeight={"bold"}
                  fontSize={"24px"}
                  margin={"20px"}
                >
                  {`Error: ${errorMsg}`}
                </Text>
              </Center>
            </form>
          )}
        </ModalBody>
        <ModalFooter>
          <Center width="100%">
            <Button
              width={"50%"}
              colorScheme={"green"}
              isDisabled={errorMsg != null}
              onClick={async () => {
                if (props.editRow?.signedupSoftDeleted) {
                  props
                    .onRestoreDeleted(props.editRow.signedupId!)
                    .then(() => {
                      toast(`Anmälan återställd!`, {
                        type: "success",
                        position: toast.POSITION.TOP_RIGHT,
                      });
                      props.onClose(true);
                    })
                    .catch((e) => {
                      toast(`Kunde inte återställa anmälan...`, {
                        type: "error",
                        position: toast.POSITION.TOP_RIGHT,
                      });
                      props.onClose(false);
                    });
                } else {
                  if (errorMsg != null) {
                    return;
                  }

                  props
                    .onUpdate(
                      {
                        disciplineId: discipline?.id ?? undefined,
                        groupName: props.editRow?.groupName, // Has no effect
                        firstname: firstname,
                        lastname: lastname,
                        fullBirthYear: birthdate!.getFullYear(),
                        sportsTeam: sportsteam,
                        socialSecurityNumber: updateSocialSecurityNumberWithNewDate(props.editRow?.socialSecurityNumber, birthdate!) ?? undefined, 
                        gender: gender?.toString(),
                      },
                      props.editRow!.signedupId!
                    )
                    .then(async (response) => {
                      if (
                        props.editRow?.groupName &&
                        group !== props.editRow?.groupName &&
                        group.trim().length >= 3
                      ) {
                        return props
                          .onUpdateGroupName(
                            props.editRow.groupName,
                            group,
                            props.editRow.paymentId!
                          )
                          .then(() => {
                            toast(`Lagnamnet är uppdaterat!`, {
                              type: "success",
                            });
                          })
                          .catch((e) => {
                            toast(`Lagnamnet kunde inte uppdateras`, {
                              type: "error",
                            });
                            throw e;
                          });
                      }
                      return;
                    })
                    .then(() => {
                      toast(`Anmälan uppdaterad`, { type: "success" });
                    })
                    .catch(() => {
                      toast(`Uppdateringen misslyckades`, { type: "error" });
                    })
                    .finally(() => {
                      props.onClose(true);
                    });
                }
              }}
            >
              {props.editRow?.signedupSoftDeleted ? "Återställ" : "Spara"}
            </Button>
            {session.hasAtLeastRole(Role.OFFICIAL) && (
              <Button
                width={"50%"}
                margin={"20px"}
                colorScheme={"red"}
                onClick={async () => {
                  if (props.editRow?.signedupSoftDeleted) {
                    props.onClose(false);
                  } else {
                    props
                      .onDelete(props.editRow!.signedupId!)
                      .then(() => {
                        toast(`Anmälan borttagen`, { type: "success" });
                        props.onClose(true);
                      })
                      .catch(() => {
                        toast(`Borttagningen misslyckades`, {
                          type: "error",
                        });
                        props.onClose(false);
                      });
                  }
                }}
              >
                {props.editRow?.signedupSoftDeleted
                  ? "Avbryt"
                  : "Ta bort anmälan"}
              </Button>
            )}
          </Center>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}


function dateFromSocialSecurityNumber(ssn: string | undefined | null): Date | null {
  if (!ssn) {
    return null;
  }

  const match = ssn.match(/^(\d{8})-([xX0-9]{4})$/);
  if (!match) {
    return null;
  }

  const [, birthDatePart,] = match;
  const year = parseInt(birthDatePart.slice(0, 4), 10);
  const month = parseInt(birthDatePart.slice(4, 6), 10) - 1;
  const day = parseInt(birthDatePart.slice(6, 8), 10);

  return new Date(year, month, day);
}

function dateFromYear(year: number | undefined | null): Date | null {
  if (!year) return null;
  return new Date(year, 0, 1); // First day of the year
}


function updateSocialSecurityNumberWithNewDate(ssn: string | undefined | null, newDate: Date | null): string | null {
  if (!ssn || !newDate) return null;

  const birthDayPart = birthDateToString(newDate);

  const match = ssn.match(/^(\d{8})-([xX0-9]{4})$/);
  if (!match) return `${birthDayPart}-XXXX`;

  const [, , lastFourDigitsPart] = match;
  return `${birthDayPart}-${lastFourDigitsPart}`;
}